import Legend from 'components/Charts/Legend/Legend';
import './PitchTypeUsageChart.scss';

import { DrawableChartConfigDto, PitchGrouping, PitchTypeUsageChartDataDto } from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import { pitchTypeGroupToColor, pitchTypeToColor } from 'utilities/colorHelpers';
import { useCallback, useMemo } from 'react';
import { useChartDataDisplay } from 'hooks/helperHooks';
import { Tooltip, Typography } from '@trackman/web-shared-components';
import { useTranslation } from 'react-i18next';
import { pitchTypeGroupLegendItems, pitchTypeGroups, pitchTypeLegendItems, pitchTypes } from 'utilities/constants';

type PitchTypeUsageChartProps = ChartProps<PitchTypeUsageChartDataDto, DrawableChartConfigDto> & {
  pitchGrouping: PitchGrouping;
};

const PitchTypeUsageChart = ({ chartData, isLoading, chartConfig, pitchGrouping }: PitchTypeUsageChartProps) => {
  const { t } = useTranslation();

  const simulateChartData = useCallback((isLoading: boolean) => {
    const simulatedChartData: PitchTypeUsageChartDataDto = {
      totalPitchCount: 0,
      pitchTypeUsages: [],
    };

    if (isLoading) {
      const pitchTypeLabels = pitchGrouping === 'Types' ? pitchTypes : pitchTypeGroups;
      for (const pitchType of pitchTypeLabels.filter((pitchType) => pitchType !== 'Undefined')) {
        const count = Math.floor(Math.random() * 9) + 2;
        simulatedChartData.pitchTypeUsages.push({ pitchTypeOrGroup: pitchType, count });
        simulatedChartData.totalPitchCount += count;
      }
    }

    return simulatedChartData;
  }, []);

  const chartDataDisplay = useChartDataDisplay(chartData, simulateChartData, isLoading);

  const sortedPitchUsages = useMemo(
    () => chartDataDisplay.pitchTypeUsages.sort((a, b) => a.pitchTypeOrGroup.localeCompare(b.pitchTypeOrGroup)),
    [chartDataDisplay.pitchTypeUsages]
  );

  const filteredLegendItems = useMemo(
    () =>
      (pitchGrouping === 'Types' ? pitchTypeLegendItems : pitchTypeGroupLegendItems).filter((item) =>
        chartDataDisplay.pitchTypeUsages.map((x) => x.pitchTypeOrGroup).includes(item.label)
      ),
    [chartDataDisplay]
  );

  return (
    <div className='PitchTypeUsageChart-container'>
      <div className='PitchTypeUsageChart-bar-wrapper'>
        <div className='PitchTypeUsageChart-bar'>
          {sortedPitchUsages.map((usage) => (
            <Tooltip key={usage.pitchTypeOrGroup} title={`${t(usage.pitchTypeOrGroup)}: ${usage.count}`}>
              <div
                className='PitchTypeUsageChart-bar-item'
                style={{
                  width: `${(usage.count / chartDataDisplay.totalPitchCount) * 100}%`,
                  backgroundColor: `${pitchTypeToColor[usage.pitchTypeOrGroup] ?? pitchTypeGroupToColor[usage.pitchTypeOrGroup]}80`,
                  borderColor: pitchTypeToColor[usage.pitchTypeOrGroup] ?? pitchTypeGroupToColor[usage.pitchTypeOrGroup],
                }}
              />
            </Tooltip>
          ))}
        </div>
        <div className='PitchTypeUsageChart-total-wrapper'>
          <Typography variant='body' component='div'>
            {t('Total')}:
          </Typography>
          <Typography variant='h1' component='div' className='PitchTypeUsageChart-total-value'>
            {chartDataDisplay.totalPitchCount || '-'}
          </Typography>
        </div>
      </div>
      <Legend items={filteredLegendItems} />
    </div>
  );
};

export default PitchTypeUsageChart;
